<template>
  <div
    class="projectCode"
    @click="handleLoginCheck"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="realRules"
      @submit.native.prevent
    >
      <el-row
        class="row"
        align="middle"
        :gutter="20"
      >
        <el-col
          class="stepTitle"
          :span="4"
        >
          <span class="label">{{ $t('projectCode.projectName') }}</span>
        </el-col>
        <el-col
          :span="7"
          :offset="2"
        >
          <el-form-item prop="projectName">
            <el-input
              :readonly="!hasAccess"
              v-model="form.projectName"
              :placeholder="$t('projectCode.inputProjectName')"
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="10">
          <span class="info">{{ $t('projectCode.supportedLanguages') }}</span>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col
          :span="4"
          class="stepTitle"
        >
          <span class="label">{{ $t('projectCode.language') }}</span>
        </el-col>
        <el-col
          :span="7"
          :offset="2"
        >
          <el-select
            style="width: 100%;display: block;"
            v-model="form.language"
            :disabled="!hasAccess"
          >
            <el-option
              label="Java"
              value="JAVA"
            />
            <el-option
              label="JavaScript/TypeScript"
              value="JAVASCRIPT"
            />
            <el-option
              label="Python"
              value="PYTHON"
            />
          </el-select>
        </el-col>
      </el-row>
    </el-form>
    <el-row
      class="fileUploadRow"
      :gutter="20"
      v-if="form.language"
    >
      <el-col
        :span="4"
        class="stepTitle"
      >
        <span class="label">
          {{ getUploadLabel }}
        </span>
      </el-col>
      <el-col
        :span="7"
        :offset="2"
      >
        <file-upload
          ref="fileUpload"
          :name="fileName"
          :placeholder="uploadInstructions"
          :accept="acceptedFileTypes"
          :maxSize="maxSize"
          @start="handleStart"
          @end="handleEnd"
          type="box"
          :disabled="!hasAccess"
        />
        <div
          class="progressBar"
          v-if="status === 'progress'"
        >
          <progress-bar key="progress"></progress-bar>
          <div class="progressTips">{{ $t('projectCode.uploading') }}
            <span class="fileName">
              {{fileName}}
            </span>
            <span>...</span>
          </div>
        </div>
        <div
          class="progressBar"
          v-if="status === 'success'"
        >
          <progress-bar
            :done="true"
            key="success"
          ></progress-bar>
          <div class="progressTips">{{ $t('projectCode.uploadSuccess') }}</div>
        </div>
        <div
          class="progressBar"
          v-if="status === 'error'"
        >
          <progress-bar
            :done="true"
            key="error"
            status="exception"
          ></progress-bar>
          <div class="progressTips">
            <i18n path="projectCode.uploadFailed">
              <template
                #1
                v-if="errorMessage"
              >
                <span class="errorMessage">"{{errorMessage}}"</span>
              </template>
              <template #0>
                {{$t('common.feedback.tryAgain')}}
                {{` ${$t('common.feedback.or')} `}}
                <i18n path="common.feedback.method1">
                  <a :href="`mailto:${SUPPORT_EMAIL}`">{{SUPPORT_EMAIL}}</a>
                </i18n>
                {{` ${$t('common.feedback.or')} `}}
                <i18n path="common.feedback.method2">
                  <a
                    :href="`${DISCORD_URL}`"
                    target="_blank"
                  >{{DISCORD_URL}}</a>
                </i18n>
              </template>
            </i18n>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FileUpload from '@/components/file-upload';
import ProgressBar from '@/components/progress-bar.vue';
import EventBus from '@/lib/events';
import { formatFileSize } from '@/utils/file-size';
import { LANGUAGE_TYPES } from '@/enum';
// 添加常量定义

export default {
  name: 'ProjectCode',
  components: {
    FileUpload,
    ProgressBar,
  },
  props: {
    payload: Object,
  },
  data() {
    const { fileName, fileSha, projectName, lang } = this.payload || {};

    return {
      form: {
        projectName: projectName || '',
        language: lang || '',
      },
      rules: {
        projectName: [{ required: true, message: this.$t('projectCode.inputProjectName'), trigger: 'blur' }],
        language: [{ required: true, message: this.$t('projectCode.selectLanguage'), trigger: 'change' }],
      },
      status: 'init',
      fileName: fileName || '',
      fileSha: fileSha || '',
      maxSize: 1024 * 1024 * 500, // 500MB
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters('user', ['hasAccess', 'isAuthenticated']),
    maxSizeStr() {
      return formatFileSize(this.maxSize);
    },
    realRules() {
      if (this.hasAccess) {
        return this.rules;
      }

      return {};
    },
    getUploadLabel() {
      switch (this.form.language) {
        case LANGUAGE_TYPES.JAVA:
          return this.$t('projectCode.javaArchive');
        case LANGUAGE_TYPES.JAVASCRIPT:
        case LANGUAGE_TYPES.PYTHON:
          return this.$t('projectCode.javascriptArchive');
        default:
          return this.$t('projectCode.javaArchive');
      }
    },
    uploadInstructions() {
      if (this.form.language === LANGUAGE_TYPES.JAVA) {
        return this.$t('projectCode.uploadJavaInstructions', { maxSize: this.maxSizeStr });
      } else if (this.form.language === LANGUAGE_TYPES.JAVASCRIPT) {
        return this.$t('projectCode.uploadJavaScriptInstructions', { maxSize: this.maxSizeStr });
      } else if (this.form.language === LANGUAGE_TYPES.PYTHON) {
        return this.$t('projectCode.uploadPythonInstructions', { maxSize: this.maxSizeStr });
      }
      return this.$t('projectCode.uploadInstructions', { maxSize: this.maxSizeStr });
    },
    acceptedFileTypes() {
      return this.form.language === LANGUAGE_TYPES.JAVA ? '.jar,.war' : '.zip';
    },
  },
  watch: {
    status() {
      if (this.status === 'progress') {
        this.$emit('next-loading', true);
      } else {
        this.$emit('next-loading', false);
      }
    },
    'form.projectName': {
      immediate: true,
      handler(newVal) {
        if (this.hasAccess) {
          if (!newVal) {
            this.$emit('next-disabled', true);
          } else {
            this.$emit('next-disabled', !this.fileSha);
          }
        }
      },
    },
    fileSha: {
      immediate: true,
      handler(val) {
        if (this.hasAccess) {
          if (!val) {
            this.$emit('next-disabled', true);
          } else {
            this.$emit('next-disabled', !this.form.projectName);
          }
        }
      },
    },
    'form.language': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.fileSha = '';
          this.fileName = '';
          this.status = 'init';
          this.$refs.fileUpload.clear();
        }
      },
    },
  },
  methods: {
    handleStart({ fileName }) {
      this.status = 'progress';
      this.fileName = fileName;
      this.errorMessage = '';
    },
    handleEnd(type, res) {
      if (type === 'success') {
        this.status = 'success';
        this.fileSha = res?.sha256 || '';
      } else {
        this.status = 'error';
        this.errorMessage = res?.message || '';
      }
    },
    next() {
      if (this.form.projectName === '') {
        this.$message.error(this.$t('projectCode.inputProjectName'));
        return;
      }

      if (!this.fileSha) {
        this.$message.error(this.$t('projectCode.inputJavaArchive'));
        return;
      }

      if (this.status === 'progress') {
        this.$message.error(this.$t('projectCode.waitForUpload'));
        return;
      }

      if (this.status === 'error') {
        this.$message.error(this.$t('projectCode.tryAgain'));
        return;
      }

      if (this.fileSha) {
        return {
          stage: 'ProjectCode',
          data: {
            fileName: this.fileName,
            fileSha: this.fileSha,
            projectName: this.form.projectName,
            lang: this.form.language,
          },
        };
      }
    },
    handleLoginCheck() {
      if (!this.isAuthenticated) {
        EventBus.$emit('signIn');
        return;
      }

      if (!this.hasAccess) {
        EventBus.$emit('noAccess');
      }
    },
  },
};
</script>

<style lang="scss">
.projectCode {
  .label {
    font-weight: bold;
    line-height: 40px;
    white-space: nowrap;
  }

  .errorMessage {
    color: #f56c6c;
  }

  .info {
    line-height: 40px;
  }

  .progressBar {
    margin-top: 40px;
  }

  .progressTips {
    display: flex;
    justify-content: center;
    margin-top: 10px;
  }

  .fileName {
    margin: 0 8px;
    display: inline-block;
    font-weight: bold;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fileUploadRow {
    margin-top: 20px;
  }
}
</style>